import { useState, useEffect, useCallback } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { RepositorySelector } from './components/RepositorySelector';
import { ComponentInfoCard } from './components/ComponentInfo';
import { BlackduckIntegrationCard } from './components/BlackduckIntegration';
import { InfrastructureSelectionCard } from './components/InfrastructureSelection';
import { SechubIntegrationCard } from './components/SechubIntegration';
import { SonarQubeIntegrationCard } from './components/SonarQubeIntegration';
import { PagerDutyIntegrationCard } from './components/PagerDutyIntegration';
import { YamlPreviewCard } from './components/YamlPreview';
import { APISelectionCard } from './components/APISelection';
import { WelcomeCard } from './components/WelcomeCard';
import { RepositoryOnboardingProvider } from './context/RepositoryOnboardingContext';
import { useRepositoryData } from './hooks/useRepositoryData';
import { YamlDataProvider } from './components/YamlDataProvider';
import { ComponentInfoData } from './components/ComponentInfo/types';
import { SechubData } from './components/SechubIntegration/types';
import { BlackduckData } from './components/BlackduckIntegration/types';
import { SonarQubeData } from './components/SonarQubeIntegration/types';
import { PagerDutyData } from './components/PagerDutyIntegration/types';

export const RepositoryOnboarding = (
  props: FieldExtensionComponentProps<{
    value: string;
    infrastructureDependencies?: string[];
    providesApis?: string[];
    catalogEntityYaml?: string;
  }>,
) => {
  const {
    onChange, // Function to update form data
    formData, // Current field value
    uiSchema, // UI schema configuration
    disabled = false,
  } = props;

  // Get repository data from the catalog
  const { repositories, loading, error, searchRepositories } =
    useRepositoryData();

  // Local state management - extract values from object or use defaults
  const [localValue, setLocalValue] = useState(formData?.value ?? '');
  const [localInfrastructureDependencies, setLocalInfrastructureDependencies] =
    useState<string[]>(formData?.infrastructureDependencies ?? []);
  const [localProvidesApis, setLocalProvidesApis] = useState<string[]>(
    formData?.providesApis ?? [],
  );

  // Shared state for editable data across all cards
  const [componentInfoData, setComponentInfoData] =
    useState<ComponentInfoData | null>(null);
  const [sechubData, setSechubData] = useState<SechubData | null>(null);
  const [blackduckData, setBlackduckData] = useState<BlackduckData | null>(
    null,
  );
  const [sonarqubeData, setSonarqubeData] = useState<SonarQubeData | null>(
    null,
  );
  const [pagerdutyData, setPagerdutyData] = useState<PagerDutyData | null>(
    null,
  );

  // State to hold YAML string generated inside provider context
  const [yamlString, setYamlString] = useState<string>('');

  // Callback to update YAML string from provider context
  const handleYamlChange = (newYamlString: string) => {
    setYamlString(newYamlString);
  };

  // Update local state when formData changes
  useEffect(() => {
    setLocalValue(formData?.value || '');
    setLocalInfrastructureDependencies(
      formData?.infrastructureDependencies ?? [],
    );
    setLocalProvidesApis(formData?.providesApis ?? []);
  }, [formData]);

  // Handle repository value changes
  const handleChange = (newValue: string) => {
    setLocalValue(newValue);
    // Clear infrastructure dependencies and API selections when repository changes
    setLocalInfrastructureDependencies([]);
    setLocalProvidesApis([]);
    // Reset all integration data when repository changes
    setComponentInfoData(null);
    setSechubData(null);
    setBlackduckData(null);
    setSonarqubeData(null);
    setPagerdutyData(null);
    onChange({
      value: newValue,
      infrastructureDependencies: [],
      providesApis: [],
      catalogEntityYaml: '', // Will be updated once data is loaded
    });
  };

  // Handle infrastructure dependencies changes
  const handleInfrastructureChange = useCallback(
    (selectedInfrastructures: string[]) => {
      setLocalInfrastructureDependencies(selectedInfrastructures);
      onChange({
        value: localValue,
        infrastructureDependencies: selectedInfrastructures,
        providesApis: localProvidesApis,
        catalogEntityYaml: yamlString,
      });
    },
    [localValue, localProvidesApis, yamlString, onChange],
  );

  // Handle API selection changes
  const handleApiChange = useCallback(
    (selectedApis: string[]) => {
      setLocalProvidesApis(selectedApis);
      onChange({
        value: localValue,
        infrastructureDependencies: localInfrastructureDependencies,
        providesApis: selectedApis,
        catalogEntityYaml: yamlString,
      });
    },
    [localValue, localInfrastructureDependencies, yamlString, onChange],
  );

  // Update handlers for integration data
  const handleComponentInfoUpdate = (data: ComponentInfoData) => {
    setComponentInfoData(data);
  };

  const handleSechubUpdate = (data: SechubData | null) => {
    setSechubData(data);
  };

  const handleBlackduckUpdate = (data: BlackduckData | null) => {
    setBlackduckData(data);
  };

  const handleSonarQubeUpdate = (data: SonarQubeData | null) => {
    setSonarqubeData(data);
  };

  const handlePagerDutyUpdate = (data: PagerDutyData | null) => {
    setPagerdutyData(data);
  };

  // Effect to update form data whenever yamlString changes
  useEffect(() => {
    if (localValue && yamlString) {
      onChange({
        value: localValue,
        infrastructureDependencies: localInfrastructureDependencies,
        providesApis: localProvidesApis,
        catalogEntityYaml: yamlString,
      });
    }
  }, [
    yamlString,
    localValue,
    localInfrastructureDependencies,
    localProvidesApis,
    onChange,
  ]);

  return (
    <FormControl fullWidth margin="normal">
      {/* Welcome Card - Explains what this field does */}
      <WelcomeCard />

      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Select a repository from the catalog
        </Typography>
      </Box>
      <RepositorySelector
        value={localValue}
        onChange={handleChange}
        repositories={repositories}
        loading={loading}
        error={error}
        onSearch={searchRepositories}
        // Lock the selector after a repository has been chosen once
        disabled={disabled || Boolean(localValue)}
        placeholder={
          uiSchema?.['ui:placeholder'] ||
          'Search repositories by name or fullName...'
        }
      />

      {/* Wrap integration cards with context provider to share data */}
      <RepositoryOnboardingProvider repositoryValue={localValue}>
        <YamlDataProvider
          repositoryValue={localValue}
          infrastructureDependencies={localInfrastructureDependencies}
          providesApis={localProvidesApis}
          componentInfoData={componentInfoData}
          sechubData={sechubData}
          blackduckData={blackduckData}
          sonarqubeData={sonarqubeData}
          pagerdutyData={pagerdutyData}
          onYamlChange={handleYamlChange}
        >
          <YamlPreviewCard
            selectedRepository={localValue}
            infrastructureDependencies={localInfrastructureDependencies}
            providesApis={localProvidesApis}
            componentInfoData={componentInfoData}
            sechubData={sechubData}
            blackduckData={blackduckData}
            sonarqubeData={sonarqubeData}
            pagerdutyData={pagerdutyData}
          />
          <ComponentInfoCard
            selectedRepository={localValue}
            data={componentInfoData || undefined}
            onDataUpdate={handleComponentInfoUpdate}
          />
          <BlackduckIntegrationCard
            selectedRepository={localValue}
            data={blackduckData || undefined}
            onDataUpdate={handleBlackduckUpdate}
          />
          <SechubIntegrationCard
            selectedRepository={localValue}
            data={sechubData || undefined}
            onDataUpdate={handleSechubUpdate}
          />
          <SonarQubeIntegrationCard
            selectedRepository={localValue}
            data={sonarqubeData || undefined}
            onDataUpdate={handleSonarQubeUpdate}
          />
          <PagerDutyIntegrationCard
            selectedRepository={localValue}
            data={pagerdutyData || undefined}
            onDataUpdate={handlePagerDutyUpdate}
          />
          <InfrastructureSelectionCard
            selectedRepository={localValue}
            selectedInfrastructures={localInfrastructureDependencies}
            onInfrastructureChange={handleInfrastructureChange}
          />
          <APISelectionCard
            selectedRepository={localValue}
            selectedApis={localProvidesApis}
            onApiChange={handleApiChange}
          />
        </YamlDataProvider>
      </RepositoryOnboardingProvider>
    </FormControl>
  );
};

// Export schema for registration
export { RepositoryOnboardingSchema } from './schema';
